import * as React from 'react'
import {Box, Typography, Chip} from '@mui/material'
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'
import {alertStripSx} from '../../../../styles/patterns'
import {FONT, SIZE, WEIGHT} from '../../../../styles/foundation/typography'
import {NAVY, WARNING} from '../../../../styles/foundation/colors'
import {GAP} from '../../../../styles/foundation/spacing'
import {dims, grams, kg} from '../format'
import CartonSVG from '../components/CartonSVG'
import KeyValue from '../components/KeyValue'
import {PACK_CONDITION} from './copy'

const BRANDING_LABEL = {self: 'Self branded', generic: 'Generic'}
const KIND_LABEL = {flyer: 'Flyer', carton: 'Carton', other: 'Other'}

/**
 * The packing material a request is conditional on. Takes the RESOLVED
 * `pack` object — the caller does `packOf(PACKS, req.packId)` — never
 * `packs` + `packId`, so this component never has to know how the
 * collection is looked up. Returns null when there is no pack to show.
 *
 * `condition` renders the "this freeze only holds while..." warning strip;
 * callers pass it for approved/preliminary requests, where the condition is
 * live, and omit it for a plain "what was asked for" recap.
 */
export default function RqPackBlock({pack, condition = false}) {
  if (!pack) return null

  const items = [
    {label: 'Dimensions', value: pack.dims ? dims(pack.dims) : 'Not applicable to a flyer'},
    {label: 'Self weight', value: grams(pack.selfWeightG)},
    {label: 'Its volumetric weight', value: pack.volKg != null ? kg(pack.volKg) : '—'},
  ]

  return (
    <Box sx={{display: 'grid', gap: GAP.sm}}>
      <Box sx={{display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap'}}>
        <Typography sx={{fontFamily: FONT, fontWeight: WEIGHT.semibold, fontSize: SIZE.sm, color: NAVY}}>
          {pack.label}
        </Typography>
        {/* The product master has no branding or type column, so a live pack carries neither. An
            empty chip is a grey pill saying nothing, so it is simply not drawn. */}
        {(BRANDING_LABEL[pack.branding] || pack.branding) && (
          <Chip
            size='small'
            label={BRANDING_LABEL[pack.branding] || pack.branding}
            sx={{fontFamily: FONT, fontSize: SIZE['2xs']}}
          />
        )}
        {(KIND_LABEL[pack.kind] || pack.kind) && (
          <Chip
            size='small'
            label={KIND_LABEL[pack.kind] || pack.kind}
            sx={{fontFamily: FONT, fontSize: SIZE['2xs']}}
          />
        )}
      </Box>

      <Box sx={{display: 'flex', gap: GAP.lg, alignItems: 'flex-start', flexWrap: 'wrap'}}>
        <KeyValue items={items} />
        {pack.dims && <CartonSVG dims={pack.dims} size={96} />}
      </Box>

      {condition && (
        <Box sx={{...alertStripSx(WARNING), alignItems: 'flex-start'}}>
          <WarningAmberOutlinedIcon sx={{fontSize: 16, mt: '2px'}} />
          <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], lineHeight: 1.6, fontWeight: WEIGHT.regular}}>
            {PACK_CONDITION(pack.label)}
          </Typography>
        </Box>
      )}
    </Box>
  )
}
